import { useEffect, useMemo, useState } from "react";
import { CopyableEmail } from "@/components/common/CopyableEmail";
import { AlertCircle, CheckCircle, ExternalLink, Loader2, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { BaseSheet } from "@/components/common/BaseSheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { PhoneNumberAdminService } from "@/services/admin/PhoneNumberAdminService";
import type { PhoneNumberWithDetails } from "@/types/phone-numbers";
import { formatPhoneNumber, getCountryName } from "../utils/phoneNumberUtils";

interface PortingTaskDrawerProps {
  phone: PhoneNumberWithDetails | null;
  isOpen: boolean;
  onClose: () => void;
  onUpdated: () => void;
}

const Field = ({ label, value }: { label: string; value?: string | null }) => (
  <div>
    <p className="text-xs font-medium text-gray-500">{label}</p>
    <p className="text-sm text-gray-900 break-words"><CopyableEmail email={value} fallback="-" /></p>
  </div>
);

const LinkField = ({ label, value }: { label: string; value?: string | null }) => (
  <div>
    <p className="text-xs font-medium text-gray-500">{label}</p>
    {value ? (
      <a
        href={value}
        target="_blank"
        rel="noreferrer"
        className="inline-flex items-center gap-1 text-sm text-genie-primary hover:underline break-all"
      >
        Open link <ExternalLink className="h-3 w-3" />
      </a>
    ) : (
      <p className="text-sm text-gray-900">-</p>
    )}
  </div>
);

const ChecklistItem = ({ children, done = false }: { children: React.ReactNode; done?: boolean }) => (
  <li className="flex gap-2 text-sm text-gray-700">
    {done ? (
      <CheckCircle className="h-4 w-4 text-genie-primary mt-0.5" />
    ) : (
      <AlertCircle className="h-4 w-4 text-genie-accent mt-0.5" />
    )}
    <span>{children}</span>
  </li>
);

const PortingTaskDrawer = ({ phone, isOpen, onClose, onUpdated }: PortingTaskDrawerProps) => {
  const metadata = phone?.metadata || {};
  const contact = metadata.port_contact || {};
  const portability = metadata.twilio_portability;
  const apiEligible = metadata.twilio_api_eligible === true;

  const [isWorking, setIsWorking] = useState(false);
  const [adminNote, setAdminNote] = useState("");
  const [manualTicketId, setManualTicketId] = useState("");
  const [manualRequestUrl, setManualRequestUrl] = useState("");
  const [manualStatus, setManualStatus] = useState("port_pending");
  const [documentSid, setDocumentSid] = useState("");
  const [targetPortInDate, setTargetPortInDate] = useState("");
  const [accountTelephoneNumber, setAccountTelephoneNumber] = useState("");
  const [customerType, setCustomerType] = useState<"Business" | "Individual">("Business");
  const [pin, setPin] = useState("");
  const [street, setStreet] = useState("");
  const [street2, setStreet2] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [zip, setZip] = useState("");

  useEffect(() => {
    setAdminNote(metadata.admin_note || "");
    setManualTicketId(metadata.manual_ticket_id || "");
    setManualRequestUrl(metadata.manual_request_url || "");
    setManualStatus(metadata.port_status || phone?.status || "port_pending");
    setDocumentSid(metadata.twilio_document_sid || "");
    setTargetPortInDate(metadata.twilio_port_in_payload?.target_port_in_date || "");
    setAccountTelephoneNumber(contact.account_telephone_number || phone?.phone_number || "");
  }, [phone?.id]);

  const automationBlockedReason = useMemo(() => {
    if (!phone) return "";
    if (phone.country_code !== "US") return "Automated Twilio PortIn is currently limited to US non-toll-free numbers.";
    if (portability?.number_type === "TOLL-FREE") return "Toll-free numbers are not supported by Twilio PortIn API beta.";
    if (portability?.not_portable_reason) return portability.not_portable_reason;
    if (!apiEligible) return "Run portability check before creating an automated PortIn request.";
    return "";
  }, [apiEligible, phone, portability]);

  const runAction = async (action: () => Promise<any>, successMessage: string) => {
    if (!phone) return;
    setIsWorking(true);
    try {
      await action();
      toast.success(successMessage);
      onUpdated();
    } catch (error: any) {
      toast.error(error.message || "Porting task update failed");
    } finally {
      setIsWorking(false);
    }
  };

  if (!phone) return null;

  return (
    <BaseSheet
      isOpen={isOpen}
      onClose={onClose}
      title="Manage Porting"
      description={`${formatPhoneNumber(phone.phone_number)} for ${phone.agent_name || "genie"}`}
      size="xl"
      className="space-y-6"
    >
      <section className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field label="Phone Number" value={formatPhoneNumber(phone.phone_number)} />
        <Field label="Country" value={phone.country_code ? `${getCountryName(phone.country_code)} (${phone.country_code})` : null} />
        <Field label="Genie" value={phone.agent_name} />
        <Field label="Customer" value={phone.user_email || phone.user_name} />
        <Field label="Status" value={phone.status} />
        <Field label="Port Status" value={metadata.port_status || phone.status} />
      </section>

      <section className="rounded-lg border border-gray-200 p-4">
        <h3 className="text-sm font-semibold text-gray-900 mb-3">Customer Submission</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Business Name" value={contact.business_name} />
          <Field label="Authorized Representative" value={contact.authorized_representative} />
          <Field label="Contact Email" value={contact.contact_email} />
          <Field label="Current Carrier" value={contact.current_carrier} />
          <Field label="Account Number" value={contact.account_number} />
          <Field label="Billing Address" value={contact.billing_address} />
          <LinkField label="Authorization Document" value={contact.authorization_document_url} />
          <LinkField label="Latest Bill" value={contact.latest_bill_url} />
        </div>
      </section>

      <section className="rounded-lg border border-gray-200 p-4">
        <h3 className="text-sm font-semibold text-gray-900 mb-3">Operational Checklist</h3>
        <ul className="space-y-2">
          <ChecklistItem done={!!portability}>Check Twilio portability.</ChecklistItem>
          <ChecklistItem done={!!metadata.twilio_port_in_request_sid || !!manualTicketId}>
            Create Twilio port request or support ticket.
          </ChecklistItem>
          <ChecklistItem done={metadata.twilio_port_in_status === "Waiting for Signature"}>
            Wait for LOA signature when required.
          </ChecklistItem>
          <ChecklistItem done={phone.status === "port_pending"}>Monitor carrier approval and port date.</ChecklistItem>
          <ChecklistItem done={phone.status === "active"}>
            Complete attachment after the number appears in the HelpGenie Twilio account.
          </ChecklistItem>
        </ul>
      </section>

      <section className="rounded-lg border border-gray-200 p-4 space-y-4">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="text-sm font-semibold text-gray-900">Twilio Automation</h3>
            <p className="text-xs text-gray-500">Available only for US non-toll-free numbers supported by Twilio PortIn API beta.</p>
          </div>
          <Button
            type="button"
            variant="outline"
            disabled={isWorking}
            onClick={() => runAction(() => PhoneNumberAdminService.checkPortability(phone.id), "Portability checked")}
          >
            {isWorking ? <Loader2 className="h-4 w-4 animate-spin" /> : "Check Portability"}
          </Button>
        </div>

        {portability && (
          <div className="rounded-lg bg-gray-50 border border-gray-200 p-3 text-sm text-gray-700">
            <p>Portable: {String(portability.portable)}</p>
            <p>Number type: {portability.number_type || "-"}</p>
            <p>Reason: {portability.not_portable_reason || "-"}</p>
          </div>
        )}

        {automationBlockedReason && <p className="text-xs text-gray-500">{automationBlockedReason}</p>}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Input placeholder="Twilio Utility Bill Document SID" value={documentSid} onChange={(e) => setDocumentSid(e.target.value)} />
          <Input type="date" value={targetPortInDate} onChange={(e) => setTargetPortInDate(e.target.value)} />
          <Input placeholder="Account telephone number" value={accountTelephoneNumber} onChange={(e) => setAccountTelephoneNumber(e.target.value)} />
          <Input placeholder="PIN (if required)" value={pin} onChange={(e) => setPin(e.target.value)} />
          <Select value={customerType} onValueChange={(value) => setCustomerType(value as "Business" | "Individual")}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="Business">Business</SelectItem>
              <SelectItem value="Individual">Individual</SelectItem>
            </SelectContent>
          </Select>
          <Input placeholder="Street" value={street} onChange={(e) => setStreet(e.target.value)} />
          <Input placeholder="Street 2" value={street2} onChange={(e) => setStreet2(e.target.value)} />
          <Input placeholder="City" value={city} onChange={(e) => setCity(e.target.value)} />
          <Input placeholder="State" value={state} onChange={(e) => setState(e.target.value)} />
          <Input placeholder="ZIP" value={zip} onChange={(e) => setZip(e.target.value)} />
        </div>

        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            disabled={isWorking || !apiEligible}
            onClick={() =>
              runAction(
                () =>
                  PhoneNumberAdminService.createTwilioPortInRequest(phone.id, {
                    documentSid,
                    targetPortInDate,
                    customerType,
                    accountTelephoneNumber,
                    pin,
                    address: { street, street2, city, state, zip },
                  }),
                "Twilio PortIn request created",
              )
            }
          >
            Create Twilio Port Request
          </Button>
          <Button
            type="button"
            variant="outline"
            disabled={isWorking || !metadata.twilio_port_in_request_sid}
            onClick={() => runAction(() => PhoneNumberAdminService.refreshPortInStatus(phone.id), "Twilio status refreshed")}
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh Status
          </Button>
        </div>
      </section>

      <section className="rounded-lg border border-gray-200 p-4 space-y-3">
        <h3 className="text-sm font-semibold text-gray-900">Manual Workflow</h3>
        <p className="text-xs text-gray-500">
          Use this for AU/NZ, toll-free, manual-porting-required, or support-ticket cases.
        </p>

        <Accordion type="single" collapsible className="rounded-lg border border-gray-200 px-3">
          <AccordionItem value="twilio-console-steps" className="border-b-0">
            <AccordionTrigger className="text-sm font-semibold text-gray-900 py-3">
              Twilio Console steps
            </AccordionTrigger>
            <AccordionContent className="text-xs text-gray-600 space-y-3 pb-3">
              {metadata.source === "own_twilio" ? (
                <>
                  <p className="font-medium text-gray-800">
                    Customer already owns this number in their own Twilio account.
                  </p>
                  <ol className="list-decimal list-inside space-y-1.5">
                    <li>
                      Email the customer at <CopyableEmail email={contact.contact_email} fallback="the contact address on file" className="font-mono" />{" "}
                      and ask them to either (a) transfer the number to the HelpGenie Twilio sub-account, or (b) release
                      it from their account so it can be purchased fresh under HelpGenie.
                    </li>
                    <li>
                      For an account-to-account transfer: in the customer's Twilio Console go to{" "}
                      <span className="font-medium">Phone Numbers → Manage → Active Numbers</span>, select the number,
                      and use <span className="font-medium">Transfer Number</span> (Account → Sub-Accounts → Transfer
                      Numbers) targeting the HelpGenie account SID. The receiving side (HelpGenie account) must accept
                      the incoming transfer request under{" "}
                      <span className="font-medium">Account → Sub-Accounts → Pending Transfers</span>.
                    </li>
                    <li>
                      If the customer instead releases the number, wait for it to clear Twilio's release-hold window,
                      then search for and purchase it under the HelpGenie Twilio account via{" "}
                      <span className="font-medium">Phone Numbers → Buy a Number</span>.
                    </li>
                    <li>
                      Confirm the number now appears under{" "}
                      <span className="font-medium">HelpGenie account → Phone Numbers → Active Numbers</span>, then use{" "}
                      <span className="font-medium">Complete Attachment</span> below.
                    </li>
                  </ol>
                </>
              ) : (
                <>
                  <p className="font-medium text-gray-800">
                    Carrier port (not eligible for, or not covered by, the automated Twilio PortIn API above).
                  </p>
                  <ol className="list-decimal list-inside space-y-1.5">
                    <li>
                      In the Twilio Console, open{" "}
                      <span className="font-medium">Phone Numbers → Regulatory Compliance → Port Requests</span> (or{" "}
                      <span className="font-medium">Support → Porting</span> for AU/NZ/manual-only regions) and create
                      a new port-in request for {phone.phone_number ? formatPhoneNumber(phone.phone_number) : "the number"}.
                    </li>
                    <li>
                      Attach the customer-submitted documents from the Customer Submission section above: signed
                      Letter of Authorization (build from business name + authorized representative), a copy of the
                      latest bill, and current carrier/account number details.
                    </li>
                    <li>
                      Record the Twilio port request ID (or the regional carrier ticket ID) in{" "}
                      <span className="font-medium">Manual ticket/request ID</span> below, and the Twilio Console
                      request link in <span className="font-medium">Manual request URL</span>.
                    </li>
                    <li>
                      Twilio/the carrier will request a Letter of Authorization signature and a firm order commitment
                      (FOC) date — update <span className="font-medium">Manual Status</span> to reflect where it's at
                      (requested → pending) and log any carrier feedback in the admin note.
                    </li>
                    <li>
                      On the FOC date, the number activates automatically under the HelpGenie Twilio account. Verify
                      it under <span className="font-medium">Phone Numbers → Active Numbers</span>, then use{" "}
                      <span className="font-medium">Complete Attachment</span> below to import it into the genie.
                    </li>
                  </ol>
                </>
              )}
            </AccordionContent>
          </AccordionItem>
        </Accordion>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Input placeholder="Manual ticket/request ID" value={manualTicketId} onChange={(e) => setManualTicketId(e.target.value)} />
          <Input placeholder="Manual request URL" value={manualRequestUrl} onChange={(e) => setManualRequestUrl(e.target.value)} />
          <Select value={manualStatus} onValueChange={setManualStatus}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="port_requested">Port Requested</SelectItem>
              <SelectItem value="port_pending">Port Pending</SelectItem>
              <SelectItem value="failed">Failed</SelectItem>
              <SelectItem value="canceled">Canceled</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Textarea placeholder="Admin note" value={adminNote} onChange={(e) => setAdminNote(e.target.value)} rows={4} />
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            variant="outline"
            disabled={isWorking}
            onClick={() =>
              runAction(
                () =>
                  PhoneNumberAdminService.savePortingNote(phone.id, {
                    adminNote,
                    manualTicketId,
                    manualRequestUrl,
                    status: manualStatus,
                    portStatus: manualStatus,
                  }),
                "Porting note saved",
              )
            }
          >
            Save Manual Status
          </Button>
          <Button
            type="button"
            variant="outline"
            disabled={isWorking}
            onClick={() => runAction(() => PhoneNumberAdminService.cancelPortingTask(phone.id, adminNote), "Porting task canceled")}
          >
            Cancel Task
          </Button>
        </div>
      </section>

      <section className="rounded-lg border border-gray-200 p-4 space-y-3">
        <h3 className="text-sm font-semibold text-gray-900">Complete Attachment</h3>
        <p className="text-xs text-gray-500">
          Use after the number appears in the HelpGenie Twilio account. This imports the number and attaches it to the genie.
        </p>
        <Button
          type="button"
          disabled={isWorking || phone.status === "active"}
          onClick={() => runAction(() => PhoneNumberAdminService.completeExistingNumberAttachment(phone.id), "Existing number attached")}
        >
          Complete Attachment
        </Button>
      </section>
    </BaseSheet>
  );
};

export default PortingTaskDrawer;
